import React, { useEffect, useState } from "react";
import { FiInbox } from "react-icons/fi";
import { resendVerificationApi, verifyEmailApi } from "../../services/authApi";
import { AuthButton, AuthError, AuthField, AuthHeader, AuthLink } from "./authUi";

const RESEND_COOLDOWN_SECONDS = 30;

interface VerifyEmailProps {
  email: string;
  // Called once the code is accepted; signs the user in.
  onVerified: () => Promise<void>;
  onBack: () => void;
}

const VerifyEmail: React.FC<VerifyEmailProps> = ({ email, onVerified, onBack }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError("Enter the 6-digit code from the email.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await verifyEmailApi(email, trimmed);
      await onVerified();
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || "That code didn't work. Try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setInfo("");
    try {
      await resendVerificationApi(email);
      setInfo("We sent a new code.");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Couldn't send a new code. Try again.");
    }
  };

  return (
    <>
      <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-500/15 text-blue-300">
        <FiInbox size={24} />
      </div>
      <AuthHeader
        title="Check your email"
        subtitle={
          <>
            We sent a 6-digit code to <strong className="text-slate-200">{email}</strong>. Enter it
            to finish setting up your account.
          </>
        }
      />
      <form onSubmit={handleSubmit} noValidate>
        <AuthError message={error} />
        <AuthField
          label="Verification code"
          name="code"
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          placeholder="123456"
          value={code}
          onChange={(e) => {
            setError("");
            setCode(e.target.value.replace(/\D/g, ""));
          }}
          style={{ letterSpacing: "0.4em", fontVariantNumeric: "tabular-nums" }}
        />
        <AuthButton loading={loading}>Verify and continue</AuthButton>
      </form>
      {info && (
        <p role="status" className="mt-3 text-center text-sm text-emerald-300">
          {info}
        </p>
      )}

      <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-5 text-sm text-slate-400">
        {cooldown > 0 ? (
          <span>Resend code in {cooldown}s</span>
        ) : (
          <AuthLink onClick={handleResend}>Resend code</AuthLink>
        )}
        <AuthLink onClick={onBack}>Use a different email</AuthLink>
      </div>
      <p className="mt-3 text-center text-xs text-slate-500">
        Can't find it? Check your spam or promotions folder.
      </p>
    </>
  );
};

export default VerifyEmail;
